import { Flex, SkeletonCircle } from '@chakra-ui/react';
import Balance from '@mui/icons-material/Balance';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Comment from '@mui/icons-material/Comment';
import SpokeOutlined from '@mui/icons-material/SpokeOutlined';
import { format } from 'date-fns';
import Loading from '@/components/general/Loading';
import useApiFetch from '@/hooks/useApiFetch';
import { BankOfHoursBalanceResponse } from '@/services/ptos';
import staffMemberServices from '@/services/staffMember';
import { trioUITheme } from '@/styles/theme';
import { FetchApiIds } from '@/types';
import { PTORequest, PTOTypeBalance } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import { getPTORequestIntervalText, ptoTypeLabel } from '@/utils/pto';
import type { PTORequestItemLayout } from '../PTORequestItem/PTORequestItem.component';
import * as Styles from './PTORequestExpandedContent.styles';

type PTORequestExpandedContentProps = {
  ptoRequest: PTORequest;
  isLoading: boolean;
  bankOfHoursBalanceList?: BankOfHoursBalanceResponse;
  overlappingPtoRequests: PTORequest[];
  onAddCommentClick?: () => void;
  layout?: PTORequestItemLayout;
  staffMember?: StaffMember;
};

const TeamMemberPto = ({ pto }: { pto: PTORequest }): JSX.Element => {
  const { data: staffMember, isLoading: isLoadingStaffMember } = useApiFetch<StaffMember>(
    [FetchApiIds.GET_STAFF_MEMBER, pto.staffMemberId],
    async () => staffMemberServices.getStaffMember({ staffMemberId: pto.staffMemberId })
  );

  return (
    <Styles.CardTableItem>
      <Styles.TeamMemberUsercontainer>
        {isLoadingStaffMember ? (
          <SkeletonCircle width={6} height={6} />
        ) : (
          <Styles.TeamMemberInitials
            firstName={staffMember?.person.firstName || ''}
            lastName={staffMember?.person.lastName}
            pictureUrl={staffMember?.person.pictureUrl || undefined}
          />
        )}

        <Styles.TeamMemberName>
          {isLoadingStaffMember ? (
            <Styles.TeamMemberNameEmptyState />
          ) : (
            `${staffMember?.person.firstName || ''} ${staffMember?.person.lastName || ''}`
          )}
        </Styles.TeamMemberName>
      </Styles.TeamMemberUsercontainer>
      <Styles.ItemText>{getPTORequestIntervalText(pto)}</Styles.ItemText>

      <Styles.ItemText>{ptoTypeLabel[pto.ptoType]}</Styles.ItemText>
    </Styles.CardTableItem>
  );
};

const PTORequestExpandedContent = function ({
  ptoRequest,
  isLoading,
  bankOfHoursBalanceList,
  overlappingPtoRequests,
  onAddCommentClick,
  staffMember,
  layout = 'wide',
}: PTORequestExpandedContentProps): JSX.Element {
  const iconProps = { color: trioUITheme.colors['neutral-90'], height: 13, width: 12 };
  const teamName = staffMember?.team?.name || undefined;

  const getHoursFromBalance = (ptoBalance: PTOTypeBalance): string => {
    if (ptoBalance.unlimited) {
      return `${ptoBalance.usedHours} hours taken`;
    }
    return `${ptoBalance.availableHours} hours available`;
  };

  let ptosOverlappingText: string;
  if (overlappingPtoRequests.length > 0)
    ptosOverlappingText = `The following team members from the ${teamName} Team has approved overlapping PTO.`;
  else if (!teamName)
    ptosOverlappingText = `${staffMember?.person.firstName}${
      staffMember?.person.lastName ? ` ${staffMember?.person.lastName}` : ''
    } is not part of any team.`;
  else
    ptosOverlappingText = `There is no overlapping scheduled PTO for other members of the ${teamName} team.`;

  return (
    <Styles.Content data-testid="pto-request-expanded-content" $layout={layout}>
      {isLoading ? (
        <Loading
          size={24}
          thickness={6}
          sx={{ marginBlock: '68px', color: trioUITheme.colors['primary-70'] }}
        />
      ) : (
        <>
          <Styles.LeftPanel $layout={layout}>
            <Flex alignItems="center" columnGap="9px" paddingBlockEnd="8px">
              <CalendarMonth sx={iconProps} />
              <Styles.Title>Requested PTO Schedule</Styles.Title>
            </Flex>

            <Styles.CardTable>
              {(ptoRequest.entries || [])
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                .map((ptoEntry) => (
                  <Styles.CardTableItem
                    key={ptoEntry.id}
                    data-testid="pto-request-expanded-content-entry"
                  >
                    <Styles.ItemBoldText>
                      {format(new Date(ptoEntry.date), 'LLLL d, yyyy')}
                    </Styles.ItemBoldText>

                    <Styles.ItemText>{format(new Date(ptoEntry.date), 'iiii')}</Styles.ItemText>

                    <Styles.ItemText>{ptoEntry.hours} hours requested</Styles.ItemText>
                  </Styles.CardTableItem>
                ))}
            </Styles.CardTable>

            <Flex alignItems="center" columnGap="9px" paddingBlockEnd="8px">
              <Balance sx={iconProps} />
              <Styles.Title>Team Member’s PTO Balance</Styles.Title>
            </Flex>

            {bankOfHoursBalanceList && (
              <Styles.CardTable>
                {bankOfHoursBalanceList.map((balance) => (
                  <Styles.CardTableItem
                    key={balance.id}
                    data-testid="pto-request-expanded-content-balance-item"
                  >
                    <Styles.ItemBoldText>{ptoTypeLabel[balance.type]}</Styles.ItemBoldText>

                    <Styles.ItemText>{getHoursFromBalance(balance)}</Styles.ItemText>

                    <Styles.ItemBoldText lighten={balance.scheduledHours === 0}>
                      {balance.scheduledHours} hours scheduled
                    </Styles.ItemBoldText>
                  </Styles.CardTableItem>
                ))}
              </Styles.CardTable>
            )}

            <Flex alignItems="center" columnGap="9px" paddingBlockEnd="8px">
              <SpokeOutlined sx={iconProps} />
              <Styles.Title>{`${teamName ? `${teamName} ` : ''}Team`}</Styles.Title>
            </Flex>

            <Styles.CardTable>
              <Styles.ItemText>{ptosOverlappingText}</Styles.ItemText>
              {overlappingPtoRequests.map((teamMemberPTO) => (
                <TeamMemberPto key={teamMemberPTO.id} pto={teamMemberPTO} />
              ))}
            </Styles.CardTable>
          </Styles.LeftPanel>

          <Styles.RightPanel $layout={layout}>
            <Flex alignItems="center" columnGap="9px" paddingBlockEnd="8px">
              <Comment sx={iconProps} />

              <Styles.Title>Team Member’s Request Note</Styles.Title>
            </Flex>

            {ptoRequest.requestComment ? (
              <Styles.CardTable>
                <Styles.ItemText>{ptoRequest.requestComment}</Styles.ItemText>
              </Styles.CardTable>
            ) : (
              <Styles.ItemText style={{ display: 'block', marginBottom: 16 }} lighten>
                No comment.
              </Styles.ItemText>
            )}

            <Flex alignItems="center" columnGap="9px" paddingBlockEnd="8px">
              <Comment sx={iconProps} />

              <Styles.Title>Manager’s Answer Note</Styles.Title>
            </Flex>

            {ptoRequest.decisionComment ? (
              <Styles.CardTable>
                <Styles.ItemText>{ptoRequest.decisionComment}</Styles.ItemText>
              </Styles.CardTable>
            ) : (
              <>
                <Styles.ItemText style={{ display: 'block', marginBottom: 16 }} lighten>
                  No comment.
                </Styles.ItemText>
                <Styles.AddCommentText onClick={onAddCommentClick}>
                  + Add a comment
                </Styles.AddCommentText>
              </>
            )}
          </Styles.RightPanel>
        </>
      )}
    </Styles.Content>
  );
};

export default PTORequestExpandedContent;
